import * as DocumentPicker from 'expo-document-picker';
import { Plus } from 'lucide-react-native';
import { useCallback, useEffect } from 'react';
import { Keyboard } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Button, ButtonText } from './ui/button';
import { Icon } from './ui/icon';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import {
  ALLOWED_FILE_TYPES,
  FILE_CONSTRAINTS,
  getMaxFileSize,
  getMaxFileSizeMB,
  type FileItem,
} from '@/constants/fileTypes';
import { useToaster } from '@/hooks/useToaster';
import { segmentClient } from '@/util/analytics';

export interface AttachFileItem extends FileItem {}

export type AttachFileContext = 'chat' | 'inquiry_form' | 'unknown';

function getFileTypeLabel(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return 'images';
  }

  if (mimeType.startsWith('video/')) {
    return 'videos';
  }

  return 'files';
}

interface AttachFileButtonProps {
  onFileSelected: (file: AttachFileItem) => void;
  files: AttachFileItem[];
  isDisabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'solid' | 'link';
  className?: string;
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | null;
  showText?: boolean;
  context?: AttachFileContext;
  inquiryId?: number;
  conversationId?: string;
  maxFileCount?: number;
  dismissKeyboard?: boolean;
  textClassName?: string;
  triggerFilePicker?: boolean;
  onTriggerComplete?: () => void;
  isSurgeonInquiry?: boolean;
}

/**
 * Shared AttachFileButton Component
 *
 * @overview
 * Reusable file attachment button used across draft inquiry and chat interfaces.
 * Provides consistent styling, behavior, and analytics tracking for file attachments.
 *
 * @behavior
 * - Opens native document picker with allowed file type filtering
 * - Validates file type, size (25 MB per file), and count limits (max 3 files)
 * - Shows contextual error toasts for validation failures
 * - Tracks comprehensive analytics events: ATTACHMENTS_TRIGGERED and FILE_UPLOAD_FAILED
 * - Optional keyboard dismissal for form contexts
 * - Prevents duplicate file uploads by name matching
 * - Provides visual feedback for disabled state when max files reached
 * - Handles file picker cancellation gracefully
 *
 * @context
 * Allowed values: 'chat' | 'inquiry_form' | 'unknown'.
 * - 'chat': Used in chat screens; analytics include optional inquiry_id and conversation_id
 * - 'inquiry_form': Used in draft inquiry form; analytics exclude IDs prior to submission
 * - 'unknown': Fallback context; retains existing behavior with chat-style error messages
 *
 * @analytics
 * - ATTACHMENTS_TRIGGERED: Fired when user initiates file selection
 * - FILE_UPLOAD_FAILED: Fired for validation errors (file too large, duplicate, max count exceeded)
 */
export function AttachFileButton({
  onFileSelected,
  files,
  isDisabled = false,
  size = 'xs',
  variant = 'outline',
  className = 'border-dashed border-typography-300 px-1.5',
  iconSize = 'xs',
  showText = true,
  context = 'unknown',
  inquiryId,
  conversationId,
  maxFileCount = FILE_CONSTRAINTS.MAX_FILE_COUNT,
  dismissKeyboard = false,
  textClassName,
  triggerFilePicker = false,
  onTriggerComplete,
  isSurgeonInquiry = false,
}: AttachFileButtonProps) {
  const toaster = useToaster();

  const handleFileSelection = useCallback(async () => {
    if (dismissKeyboard) {
      Keyboard.dismiss();
    }

    if (files.length >= maxFileCount) {
      toaster.showError({
        title: `Maximum ${maxFileCount} files allowed`,
        message:
          context === 'inquiry_form'
            ? ERROR_MESSAGES.ATTACHMENTS.LIMIT_EXCEEDED.INQUIRY_FORM
            : ERROR_MESSAGES.ATTACHMENTS.LIMIT_EXCEEDED.CHAT,
      });

      if (context === 'chat' && inquiryId && conversationId) {
        segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
          inquiry_id: inquiryId,
          conversation_id: conversationId,
          error_title: 'max_file_count',
          limit_count: maxFileCount,
          context,
        });
      } else if (context === 'inquiry_form') {
        segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
          error_title: 'max_file_count',
          limit_count: maxFileCount,
          context,
        });
      }

      return;
    }

    try {
      const doc = await DocumentPicker.getDocumentAsync({
        multiple: false,
        type: ALLOWED_FILE_TYPES,
      });

      const file = doc.assets?.[0];

      if (doc.canceled || !file) {
        return;
      }

      const { size: fileSize, mimeType, file: fileData, uri, name } = file;

      if (!fileSize || !mimeType || !uri || !name) {
        return;
      }

      if (files.find((f) => f.name === name)) {
        toaster.showError({
          title: 'File already added',
        });

        if (context === 'chat' && inquiryId && conversationId) {
          segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
            file_size: fileSize,
            mime_type: mimeType,
            file_name: name,
            inquiry_id: inquiryId,
            conversation_id: conversationId,
            error_title: 'file_already_uploaded',
            context,
          });
        } else if (context === 'inquiry_form') {
          segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
            file_name: name,
            file_size: fileSize,
            mime_type: mimeType,
            error_title: 'file_already_uploaded',
            context,
          });
        }

        return;
      }

      const maxSize = getMaxFileSize(mimeType);

      const maxSizeMB = getMaxFileSizeMB(mimeType);

      if (fileSize > maxSize) {
        const fileTypeLabel = getFileTypeLabel(mimeType);

        toaster.showError({
          title: `File too large (max ${maxSizeMB}MB for ${fileTypeLabel})`,
        });

        if (context === 'chat' && inquiryId && conversationId) {
          segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
            file_size: fileSize,
            mime_type: mimeType,
            file_name: name,
            inquiry_id: inquiryId,
            conversation_id: conversationId,
            error_title: 'file_too_large',
            limit_size: maxSizeMB,
            context,
          });
        } else if (context === 'inquiry_form') {
          segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
            file_name: name,
            file_size: fileSize,
            mime_type: mimeType,
            error_title: 'file_too_large',
            limit_size: maxSizeMB,
            context,
          });
        }

        return;
      }

      if (context === 'chat') {
        segmentClient.track(ANALYTICS_EVENTS.ATTACHMENTS_TRIGGERED, {
          ...(inquiryId ? { inquiry_id: inquiryId } : {}),
          ...(conversationId ? { conversation_id: conversationId } : {}),
          current_file_count: files.length + 1, // +1 because we're about to add this file
          context,
        });
      } else if (context === 'inquiry_form') {
        segmentClient.track(ANALYTICS_EVENTS.ATTACHMENTS_TRIGGERED, {
          current_file_count: files.length + 1, // +1 because we're about to add this file
          is_surgeon_inquiry: isSurgeonInquiry,
          context,
        });
      }

      onFileSelected({
        name,
        size: fileSize,
        mimeType,
        file: fileData,
        uri,
      });
    } catch (error) {
      console.error('File picker error:', error);

      toaster.showError({
        title: 'Failed to select file',
      });
    }
  }, [
    context,
    inquiryId,
    conversationId,
    files,
    maxFileCount,
    dismissKeyboard,
    toaster,
    onFileSelected,
  ]);

  useEffect(() => {
    if (triggerFilePicker) {
      handleFileSelection();

      onTriggerComplete?.();
    }
  }, [triggerFilePicker, onTriggerComplete, handleFileSelection]);

  const isMaxFilesReached = files.length >= maxFileCount;

  return (
    <Button
      variant={variant}
      size={size}
      onPress={handleFileSelection}
      isDisabled={isDisabled}
      className={twJoin(
        className,
        isMaxFilesReached ? 'opacity-50' : '',
        'w-full justify-center border-typography-400 hover:bg-background-100 web:w-auto'
      )}>
      {iconSize && (
        <Icon
          as={Plus}
          className={twJoin(
            'h-[18px] w-[18px] flex-shrink-0',
            showText ? 'mr-2' : '',
            isMaxFilesReached ? 'text-typography-400' : 'text-typography-500'
          )}
        />
      )}
      {showText && (
        <ButtonText
          className={
            textClassName ||
            (isMaxFilesReached ? 'text-typography-400' : 'font-medium text-sm text-typography-500')
          }>
          Attach files
        </ButtonText>
      )}
    </Button>
  );
}
